import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { getMeta, RouteContext, SITE_NAME, TWITTER_HANDLE, THEME_COLOR } from '@/lib/seo';

interface SEOProps {
  /** Optional override path; defaults to current location.pathname */
  path?: string;
  /** Data passed to getMeta() (tool, post, article, job, event, etc.) */
  data?: Record<string, any>;
  /** Optional manual override (rare; use data instead when possible) */
  overrides?: Partial<{
    title: string;
    description: string;
    canonical: string;
    ogImage: string;
    robots: string;
    ogType: 'website' | 'article' | 'product' | 'profile';
  }>;
}

/**
 * Drop-in <SEO /> component. Renders title, description, canonical,
 * Open Graph, Twitter, robots, theme-color and author tags.
 *
 * Use one per page near the top of the JSX:
 *   <SEO data={{ tool }} />
 */
export function SEO({ path, data, overrides }: SEOProps) {
  const location = useLocation();
  const pathname = path ?? location.pathname;
  const ctx: RouteContext = { pathname, data };
  const meta = getMeta(ctx);

  const title = overrides?.title ?? meta.title;
  const description = overrides?.description ?? meta.description;
  const canonical = overrides?.canonical ?? meta.canonical;
  const ogImage = overrides?.ogImage ?? meta.ogImage;
  const robots = overrides?.robots ?? meta.robots ?? 'index,follow,max-image-preview:large';
  const ogType = overrides?.ogType ?? meta.ogType;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={robots} />
      <meta name="theme-color" content={THEME_COLOR} />
      {meta.keywords && <meta name="keywords" content={meta.keywords} />}
      {meta.author && <meta name="author" content={meta.author} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_US" />
      {meta.publishedTime && <meta property="article:published_time" content={meta.publishedTime} />}
      {meta.modifiedTime && <meta property="article:modified_time" content={meta.modifiedTime} />}
      {meta.author && <meta property="article:author" content={meta.author} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
